import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Box, useTheme, Typography, Tooltip, Chip } from '@mui/material';
import * as d3 from 'd3';
import type { NumberValue } from 'd3';

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { AccountPortfolio } from "../../../types";
import { resolveValuation } from '../../../utils/resolveValuation';
import { DOT_BREATHE_SX } from '../../../styles/realtimeAnimations';

interface PortfolioDataPoint {
    timestamp: Date;
    value: number;
}

interface PortfolioChartProps {
    portfolios: AccountPortfolio[];
    currency: 'eur' | 'usd' | 'chf';
    isLive?: boolean;
    hasPartialData?: boolean;
    partialDataTooltip?: string;
}

function getCurrencySymbol(curr: string): string {
    switch (curr) {
        case 'eur': return '€';
        case 'usd': return '$';
        case 'chf': return 'CHF ';
        default: return '';
    }
}

export const PortfolioChart: React.FC<PortfolioChartProps> = ({
    portfolios,
    currency,
    isLive,
    hasPartialData,
    partialDataTooltip,
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    const themePrimaryColor = theme.palette.primary.main || "#2F68AC";

    // Update the responsive width effect:
    useEffect(() => {
        if (!containerRef.current) return;

        // Set initial width immediately
        setWidth(containerRef.current.getBoundingClientRect().width);

        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Process portfolios and calculate metrics
    const { sortedData, startValue, currentValue, percentChange, isPositive, daysCount } = useMemo(() => {
        if (!portfolios.length) {
            return { sortedData: [], startValue: 0, currentValue: 0, percentChange: '0.00', isPositive: true, daysCount: 0 };
        }

        // Convert Portfolio[] to PortfolioDataPoint[]
        // Skip entries where valuation is unavailable for the selected currency
        const dataPoints: PortfolioDataPoint[] = portfolios
            .map(portfolio => {
                const timestamp = portfolio.timestamp;
                const value = resolveValuation(portfolio?.valuation, currency);

                // Skip entries with no valuation or invalid timestamp
                if (value == null || isNaN(timestamp.getTime()) || isNaN(value)) {
                    return null;
                }

                return { timestamp, value };
            })
            .filter((point): point is PortfolioDataPoint => point !== null);

        if (dataPoints.length === 0) {
            return { sortedData: [], startValue: 0, currentValue: 0, percentChange: '0.00', isPositive: true, daysCount: 0 };
        }

        const sorted = dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        const start = sorted[0]?.value || 0;
        const current = sorted[sorted.length - 1]?.value || 0;
        const change = start > 0 ? ((current - start) / start * 100) : 0;
        const msPerDay = 24 * 60 * 60 * 1000;
        const days = Math.round((sorted[sorted.length - 1].timestamp.getTime() - sorted[0].timestamp.getTime()) / msPerDay);

        return {
            sortedData: sorted,
            startValue: start,
            currentValue: current,
            percentChange: change.toFixed(2),
            isPositive: change >= 0,
            daysCount: days
        };
    }, [portfolios, currency]);

    useEffect(() => {
        if (!svgRef.current || sortedData.length === 0 || width === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 0, right: 0, bottom: 50, left: 0 };
        const height = 350 - margin.top - margin.bottom;
        const innerWidth = width - margin.left - margin.right;

        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        // Scales
        const x = d3
            .scaleTime()
            .domain(d3.extent(sortedData, d => d.timestamp) as [Date, Date])
            .range([0, innerWidth]);

        const yExtent = d3.extent(sortedData, d => d.value) as [number, number];
        const yPadding = (yExtent[1] - yExtent[0]) * 0.1;
        const y = d3
            .scaleLinear()
            .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
            .range([height, 0]);

        // Gradient definition
        const gradient = svg.append('defs')
            .append('linearGradient')
            .attr('id', 'portfolioGradient')
            .attr('gradientUnits', 'userSpaceOnUse')
            .attr('x1', 0).attr('y1', y(yExtent[0]))
            .attr('x2', 0).attr('y2', y(yExtent[1]));

        gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', themePrimaryColor)
            .attr('stop-opacity', 0.8);

        gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', themePrimaryColor)
            .attr('stop-opacity', 0.2);

        // Grid lines
        g.selectAll('.grid-line-horizontal')
            .data(y.ticks(5))
            .join('line')
            .attr('class', 'grid-line-horizontal')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', d => y(d))
            .attr('y2', d => y(d))
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', 0.7);

        g.selectAll('.grid-line-vertical')
            .data(x.ticks(8))
            .join('line')
            .attr('class', 'grid-line-vertical')
            .attr('x1', d => x(d))
            .attr('x2', d => x(d))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', 0.7);

        // Area
        const area = d3
            .area<PortfolioDataPoint>()
            .x(d => x(d.timestamp))
            .y0(height)
            .y1(d => y(d.value))
            .curve(d3.curveMonotoneX);

        g.append('path')
            .datum(sortedData)
            .attr('class', 'portfolio-area')
            .attr('fill', 'url(#portfolioGradient)')
            .attr('d', area);

        // Line
        const line = d3
            .line<PortfolioDataPoint>()
            .x(d => x(d.timestamp))
            .y(d => y(d.value))
            .curve(d3.curveMonotoneX);

        g.append('path')
            .datum(sortedData)
            .attr('class', 'portfolio-line')
            .attr('fill', 'none')
            .attr('stroke', themePrimaryColor)
            .attr('stroke-width', 2)
            .attr('d', line);

        // X-axis
        const tickDates = x.ticks(8);
        let lastMonth = -1;
        let lastYear = -1;

        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickValues(tickDates)
                    .tickFormat((domainValue: Date | NumberValue) => {
                        const date = new Date(domainValue as Date);
                        const month = date.getMonth();
                        const year = date.getFullYear();

                        let label: string;
                        if (lastYear === -1) {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        } else if (year !== lastYear) {
                            label = `${year}`;
                        } else if (month !== lastMonth) {
                            label = d3.timeFormat('%B')(date);
                        } else {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        }

                        lastMonth = month;
                        lastYear = year;
                        return label;
                    })
            );

        // Tooltip
        const tooltip = g.append('g')
            .attr('class', 'tooltip')
            .style('display', 'none');

        tooltip.append('rect')
            .attr('fill', 'rgba(0, 0, 0, 0.8)')
            .attr('rx', 4)
            .attr('ry', 4);

        const tooltipText = tooltip.append('text')
            .attr('fill', 'white')
            .attr('font-size', '12px');

        // Mouse interaction area
        g.append('rect')
            .attr('width', innerWidth)
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all')
            .on('mousemove', function (event) {
                const [mouseX] = d3.pointer(event, this);
                const date = x.invert(mouseX);

                // Find closest data point
                let closestPoint = sortedData[0];
                let minDistance = Math.abs(closestPoint.timestamp.getTime() - date.getTime());

                sortedData.forEach(point => {
                    const distance = Math.abs(point.timestamp.getTime() - date.getTime());
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestPoint = point;
                    }
                });

                // Show tooltip
                tooltipText.selectAll('tspan').remove();

                tooltipText.append('tspan')
                    .attr('x', 8)
                    .attr('y', 15)
                    .style('font-weight', 'bold')
                    .text(closestPoint.timestamp.toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                    }));

                tooltipText.append('tspan')
                    .attr('x', 8)
                    .attr('y', 35)
                    .text(`Portfolio Value: ${getCurrencySymbol(currency)}${closestPoint.value.toFixed(2)}`);

                const bbox = (tooltipText.node() as SVGTextElement).getBBox();
                tooltip.select('rect')
                    .attr('width', bbox.width + 16)
                    .attr('height', bbox.height + 16);

                const tooltipX = Math.min(mouseX + 10, innerWidth - bbox.width - 16);
                const tooltipY = Math.max(10, Math.min(event.offsetY - bbox.height - 20, height - bbox.height - 16));

                tooltip
                    .attr('transform', `translate(${tooltipX}, ${tooltipY})`)
                    .style('display', null);
            })
            .on('mouseout', () => tooltip.style('display', 'none'));

    }, [sortedData, currency, theme, width, themePrimaryColor]);

    if (sortedData.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No portfolio data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "350px", padding: "0" }}>
            {/* Top Section with Metrics */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 0, px: 2, pt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isLive && (
                        <Tooltip title="Receiving real-time portfolio updates" arrow>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: 'success.main',
                                        ...DOT_BREATHE_SX,
                                    }}
                                />
                                <Typography variant="body2" color="success.main">Live</Typography>
                            </Box>
                        </Tooltip>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">{daysCount}d</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <TrendingUpIcon fontSize="small" sx={{ color: isPositive ? "success.main" : "error.main" }} />
                        <Typography variant="body2" sx={{ color: isPositive ? "success.main" : "error.main" }}>
                            {isPositive ? "+" : ""}{percentChange}%
                        </Typography>
                    </Box>
                    {isLive && hasPartialData && partialDataTooltip && (
                        <Tooltip title={partialDataTooltip} arrow>
                            <Chip
                                size="small"
                                label="Partial"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Chart Section */}
            <Box ref={containerRef} sx={{ flex: 1, width: "100%", height: "100%" }}>
                <svg
                    ref={svgRef}
                    width={width}
                    height={350}
                    style={{ background: 'transparent' }}
                />
            </Box>
        </Box>
    );
};